import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Check, ChevronDown, ChevronUp, Columns3, RotateCcw } from "lucide-react";
import { Tooltip } from "@/components/atoms/Tooltip";
import { cn } from "@/lib/cn";

export interface CustomizableColumnItem {
  id: string;
  label: string;
  visible: boolean;
  locked: boolean;
  fixed: boolean;
  canHide: boolean;
  canMoveUp: boolean;
  canMoveDown: boolean;
}

interface Props {
  columns: CustomizableColumnItem[];
  onToggle: (id: string) => void;
  onMove: (id: string, direction: -1 | 1) => void;
  onReset: () => void;
}

const PANEL_WIDTH = 320;

export function ColumnCustomizer({ columns, onToggle, onMove, onReset }: Props) {
  const [open, setOpen] = useState(false);
  const [position, setPosition] = useState<{ top: number; left: number; openUp: boolean } | null>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);

  const updatePosition = useCallback(() => {
    const trigger = triggerRef.current;
    if (!trigger) return;
    const rect = trigger.getBoundingClientRect();
    const estimatedHeight = Math.min(430, 112 + columns.length * 44);
    const openUp = window.innerHeight - rect.bottom < estimatedHeight + 16 && rect.top > estimatedHeight;
    const maxTop = Math.max(8, window.innerHeight - estimatedHeight - 8);
    const top = openUp
      ? Math.min(window.innerHeight - 8, Math.max(estimatedHeight + 8, rect.top - 8))
      : Math.max(8, Math.min(maxTop, rect.bottom + 8));
    setPosition({
      top,
      left: Math.max(8, Math.min(rect.right - PANEL_WIDTH, window.innerWidth - PANEL_WIDTH - 8)),
      openUp,
    });
  }, [columns.length]);

  useLayoutEffect(() => {
    if (!open) return;
    updatePosition();
    window.addEventListener("resize", updatePosition);
    window.addEventListener("scroll", updatePosition, true);
    return () => {
      window.removeEventListener("resize", updatePosition);
      window.removeEventListener("scroll", updatePosition, true);
    };
  }, [open, updatePosition]);

  useEffect(() => {
    if (!open) return;
    const onMouseDown = (event: MouseEvent) => {
      const target = event.target as Node;
      if (triggerRef.current?.contains(target) || panelRef.current?.contains(target)) return;
      setOpen(false);
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setOpen(false);
        triggerRef.current?.focus();
      }
    };
    document.addEventListener("mousedown", onMouseDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("mousedown", onMouseDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [open]);

  return (
    <>
      <Tooltip content="Choose, reorder, or resize table columns" side="top" hoverOnly>
        <span className="inline-flex">
          <button
            ref={triggerRef}
            type="button"
            aria-haspopup="dialog"
            aria-expanded={open}
            onClick={() => setOpen((current) => !current)}
            className={cn(
              "inline-flex h-8 items-center gap-2 rounded-sm border px-3 text-sm font-medium transition-colors focus-ring",
              open
                ? "border-bridge-accent bg-bridge-accent/10 text-bridge-accent"
                : "border-bridge-border bg-bridge-surface text-bridge-fg hover:border-bridge-accent/50 hover:text-bridge-accent",
            )}
          >
            <Columns3 size={15} aria-hidden />
            Columns
          </button>
        </span>
      </Tooltip>

      {open && position && createPortal(
        <div
          ref={panelRef}
          role="dialog"
          aria-label="Customize table columns"
          className="fixed z-[300] flex max-h-[min(70vh,430px)] w-[min(320px,calc(100vw-16px))] flex-col overflow-hidden rounded-md border border-bridge-border bg-bridge-surface shadow-soft"
          style={{
            top: position.top,
            left: position.left,
            transform: position.openUp ? "translateY(-100%)" : undefined,
          }}
        >
          <div className="flex items-start justify-between gap-3 border-b border-bridge-border/70 px-4 py-3">
            <div>
              <h3 className="text-sm font-semibold text-bridge-fg">Table columns</h3>
              <p className="mt-0.5 text-xs text-bridge-muted">Show columns or change their order.</p>
            </div>
            <button
              type="button"
              onClick={onReset}
              className="inline-flex shrink-0 items-center gap-1 rounded-xs px-2 py-1 text-xs font-medium text-bridge-muted hover:bg-bridge-bg hover:text-bridge-accent focus-ring"
            >
              <RotateCcw size={13} aria-hidden /> Reset
            </button>
          </div>

          <ul className="min-h-0 flex-1 overflow-y-auto p-2 scrollbar-metal" aria-label="Available columns">
            {columns.map((column) => (
              <li key={column.id} className="flex min-h-10 items-center gap-2 rounded-sm px-2 py-1 hover:bg-bridge-bg">
                <label className={cn("flex min-w-0 flex-1 items-center gap-2", column.locked && "cursor-not-allowed")}>
                  <span className="relative flex h-4 w-4 shrink-0 items-center justify-center">
                    <input
                      type="checkbox"
                      className="peer h-4 w-4 appearance-none rounded border border-slate-400 bg-white checked:border-bridge-accent checked:bg-bridge-accent focus-visible:ring-2 focus-visible:ring-bridge-accent/40 disabled:opacity-50"
                      checked={column.visible}
                      disabled={!column.canHide}
                      onChange={() => onToggle(column.id)}
                      aria-label={`Show ${column.label} column`}
                    />
                    <Check className="pointer-events-none absolute h-3 w-3 text-white opacity-0 peer-checked:opacity-100" strokeWidth={3} aria-hidden />
                  </span>
                  <span className="truncate text-sm text-bridge-fg">{column.label}</span>
                  {column.locked && <span className="text-[10px] uppercase tracking-wide text-bridge-subtle">Required</span>}
                </label>

                <div className="flex shrink-0 items-center" aria-label={`Move ${column.label} column`}>
                  <button
                    type="button"
                    onClick={() => onMove(column.id, -1)}
                    disabled={!column.canMoveUp || column.fixed}
                    aria-label={`Move ${column.label} column left`}
                    className="rounded-xs p-1 text-bridge-muted hover:bg-white hover:text-bridge-accent focus-ring disabled:cursor-not-allowed disabled:opacity-30"
                  >
                    <ChevronUp size={15} aria-hidden />
                  </button>
                  <button
                    type="button"
                    onClick={() => onMove(column.id, 1)}
                    disabled={!column.canMoveDown || column.fixed}
                    aria-label={`Move ${column.label} column right`}
                    className="rounded-xs p-1 text-bridge-muted hover:bg-white hover:text-bridge-accent focus-ring disabled:cursor-not-allowed disabled:opacity-30"
                  >
                    <ChevronDown size={15} aria-hidden />
                  </button>
                </div>
              </li>
            ))}
          </ul>

          <p className="border-t border-bridge-border/70 px-4 py-2 text-[11px] text-bridge-muted">
            Use the arrow buttons to reorder columns. Drag a header edge to resize it.
          </p>
        </div>,
        document.body,
      )}
    </>
  );
}
